import React, {
  CSSProperties,
  ForwardRefExoticComponent,
  KeyboardEventHandler,
  ReactNode,
  RefAttributes,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import styles from './DonationFeedItem.module.css'
import {
  AmountContainerProps,
  DetailsContainerProps,
  DonationFeedItemCompanionProps,
  DonationFeedItemProps,
} from './types'
import { Card } from '../Card'
import { IconButton } from '../IconButton'
import { Keys } from '../Popup'
import { LayoutStack } from '../layouts'
import { LoadingSkeleton } from '../LoadingSkeleton'
import { Tooltip } from '../Tooltip'

type SlotComponent = React.FC<DonationFeedItemCompanionProps>
type AmountComponent = React.FC<AmountContainerProps>
type DetailsComponent = React.FC<DetailsContainerProps>
type FirstSlotComponent = SlotComponent & {
  AmountContainer: AmountComponent
}
type SecondSlotComponent = SlotComponent & {
  DetailsContainer: DetailsComponent
}

type DonationFeedItemComponent = ForwardRefExoticComponent<DonationFeedItemProps & RefAttributes<HTMLDivElement>> & {
  FirstSlot: FirstSlotComponent
  SecondSlot: SecondSlotComponent
}

function AmountSkeleton() {
  return <LoadingSkeleton width="6.25rem" height="3.523rem" className={styles.skeletonContainer} />
}

function AmountContainer({ amount, skeleton = false }: AmountContainerProps) {
  if (skeleton) return <AmountSkeleton />

  return <div className={styles.amountContainer}>{amount}</div>
}

function FirstSlot({ children }: DonationFeedItemCompanionProps) {
  return <>{children}</>
}

FirstSlot.AmountContainer = AmountContainer

const COMMENT_PREVIEW_LINES = 1

function CommentInner({
  handleEnter,
  isTooLong,
  isExpanded,
  toggleIsExpanded,
  comment,
}: {
  handleEnter: KeyboardEventHandler<HTMLInputElement>
  isTooLong: boolean
  isExpanded: boolean
  toggleIsExpanded: () => void
  comment: string
}) {
  const iconName = isExpanded ? 'chevronUp' : 'chevronDown'

  if (isExpanded || !isTooLong) {
    return (
      <>
        {comment}
        {isTooLong && (
          <IconButton
            kind="plain"
            size="default"
            iconName={iconName}
            onClick={toggleIsExpanded}
            className={styles.showMoreButton}
          />
        )}
      </>
    )
  }

  return (
    <div className={styles.clickArea} onClick={toggleIsExpanded} onKeyUp={handleEnter} role="button" tabIndex={0}>
      {comment}
      {isTooLong && <IconButton kind="plain" size="default" iconName={iconName} className={styles.showMoreButton} />}
    </div>
  )
}

function CommentWrapper({ comment }: { comment: string | undefined }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isTooLong, setIsTooLong] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) {
      setIsTooLong(false)
      return
    }

    const lineHeight = parseFloat(getComputedStyle(element).lineHeight)
    const neededLines = Math.ceil(element.scrollHeight / Math.round(lineHeight))

    setIsTooLong(neededLines > COMMENT_PREVIEW_LINES)
  }, [setIsTooLong])

  const toggleIsExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  const handleEnter: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (event.key !== Keys.Enter) return
      toggleIsExpanded()
    },
    [toggleIsExpanded]
  )

  if (!comment) return null

  return (
    <div
      ref={ref}
      className={classNames(styles.comment, { [styles.expanded!]: isExpanded })}
      style={{ '--max-preview-lines': COMMENT_PREVIEW_LINES } as CSSProperties}
    >
      <CommentInner
        handleEnter={handleEnter}
        isTooLong={isTooLong}
        isExpanded={isExpanded}
        toggleIsExpanded={toggleIsExpanded}
        comment={comment}
      />
    </div>
  )
}

function Comment({ comment }: { comment: string | undefined }) {
  return (
    <div
      className={classNames(styles.commentContainer, {
        [styles.noComment!]: !comment,
      })}
    >
      <CommentWrapper comment={comment} />
    </div>
  )
}

function Header({ header }: { header: ReactNode }) {
  const [isTruncated, setIsTruncated] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) {
      setIsTruncated(false)
      return
    }
    setIsTruncated(element.scrollWidth > Math.round(element.clientWidth))
  }, [header])

  if (isTruncated) {
    return (
      <Tooltip content={header}>
        <div className={styles.header}>{header}</div>
      </Tooltip>
    )
  }

  return (
    <div ref={ref} className={styles.header}>
      {header}
    </div>
  )
}

function DetailsSkeleton() {
  return (
    <LayoutStack className={styles.skeletonContainer} space="200">
      <LoadingSkeleton width="5.7rem" height="1.3rem" />
      <LoadingSkeleton width="5.7rem" height="1.1rem" />
    </LayoutStack>
  )
}

function DetailsContainer({ header, timestamp, comment, cta, skeleton }: DetailsContainerProps) {
  if (skeleton) return <DetailsSkeleton />
  if (cta) return <div className={styles.secondSlotWrapper}>{cta}</div>

  return (
    <LayoutStack space="none" className={styles.secondSlotWrapper}>
      <Header header={header} />
      <div className={styles.timestamp}>{timestamp}</div>
      <Comment comment={comment} />
    </LayoutStack>
  )
}

function SecondSlot({ children }: DonationFeedItemCompanionProps) {
  return <>{children}</>
}

SecondSlot.DetailsContainer = DetailsContainer

const DonationFeedItem = forwardRef(function DonationFeedItem({ children, className, style }, ref) {
  return (
    <div style={{ ...style, overflow: 'visible' }}>
      <Card className={classNames(styles.card, className)} ref={ref}>
        {children}
      </Card>
    </div>
  )
}) as DonationFeedItemComponent

DonationFeedItem.FirstSlot = FirstSlot
DonationFeedItem.SecondSlot = SecondSlot

export default DonationFeedItem
